import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  MessageSquare,
  Phone,
  Mail,
  LogOut,
  Lock,
  TrendingUp,
  Users,
  CreditCard,
  AlertTriangle,
  ExternalLink,
  Search,
  PieChart,
  Trash2,
  Sparkles,
  CheckCircle2,
  Clock,
  Calendar as CalendarIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ggg_admin_token";

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtDate(unixOrIso) {
  if (!unixOrIso) return "—";
  const d =
    typeof unixOrIso === "number"
      ? new Date(unixOrIso * 1000)
      : new Date(unixOrIso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtMoney(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function LoginForm({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onSuccess(res.data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white/[0.03] border border-white/10 rounded-sm p-8"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
          <Lock className="w-4 h-4" />
          Admin
        </div>
        <h1 className="font-display text-3xl text-white mb-2">Sign in</h1>
        <p className="text-sm text-white/60 mb-6">
          Enter the admin password to view subscriptions, payments, and contact
          submissions.
        </p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full bg-black border border-white/10 px-4 py-3 rounded-sm text-white placeholder:text-white/30 focus:border-[#5FBE1F] outline-none"
          data-testid="admin-password-input"
        />
        {error && (
          <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !password}
          className="mt-5 w-full bg-[#5FBE1F] text-black font-bold py-3 rounded-sm hover:bg-[#4A9818] hover:text-white disabled:opacity-50 transition-colors"
          data-testid="admin-login-submit"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <div
      className="bg-white border border-[#EAE5DE] rounded-sm p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-[#4A4A46]">
          {label}
        </div>
        {Icon && <Icon className="w-4 h-4 text-[#5FBE1F]" />}
      </div>
      <div className="font-display text-3xl text-[#1C1C1A] mt-2">{value}</div>
      {sub && <div className="text-xs text-[#4A4A46] mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("stripe"); // "stripe" | "contacts" | "services"
  const [overview, setOverview] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [services, setServices] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const headers = authHeaders(token);
      const [ovr, bk, subRes, payRes, contactRes, svcRes] = await Promise.all([
        axios.get(`${API}/admin/stripe/overview`, { headers }),
        axios.get(`${API}/admin/stripe/revenue-by-service`, { headers }),
        axios.get(`${API}/admin/stripe/subscriptions`, { headers }),
        axios.get(`${API}/admin/stripe/payments`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/admin/services`, { headers }),
      ]);
      setOverview(ovr.data);
      setBreakdown(bk.data);
      setSubs(subRes.data || []);
      setPayments(payRes.data || []);
      setContacts(contactRes.data || []);
      setServices(svcRes.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        setError(e?.response?.data?.detail || e?.message || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Derived: filter subscriptions by the lookup query so Mike can check
  // membership status before a visit without scrolling.
  const filteredSubs = useMemo(() => {
    if (!memberQuery.trim()) return subs;
    const q = memberQuery.trim().toLowerCase();
    return subs.filter((s) =>
      [s.customer_email, s.customer_name, s.plan_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [subs, memberQuery]);

  async function releaseSlot(row) {
    const dateBit = row.preferred_date ? ` for ${row.preferred_date}` : "";
    if (
      !window.confirm(
        `Release this slot${dateBit}? This deletes the submission from ${row.name} and frees the time window so other customers can book it.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/contact/${row.id}`, {
        headers: authHeaders(token),
      });
      setContacts((cs) => cs.filter((c) => c.id !== row.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to release slot");
    }
  }

  async function updateService(row, patch) {
    try {
      const res = await axios.patch(
        `${API}/admin/services/${row.id}`,
        patch,
        { headers: authHeaders(token) }
      );
      setServices((arr) => arr.map((s) => (s.id === row.id ? res.data : s)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update service");
    }
  }

  async function deleteService(row) {
    if (
      !window.confirm(
        `Delete this service request from ${row.customer_email}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/services/${row.id}`, {
        headers: authHeaders(token),
      });
      setServices((arr) => arr.filter((s) => s.id !== row.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete service");
    }
  }

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (!token) {
    return <LoginForm onSuccess={(t) => setToken(t)} />;
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-body">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        {/* Top bar */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5FBE1F]">
              Admin Dashboard
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter text-[#1C1C1A]">
              Glenwood Gutter Guy
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="text-sm font-semibold text-[#4A4A46] hover:text-[#1C1C1A]"
              data-testid="admin-back-link"
            >
              ← Back to site
            </a>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#1C1C1A] hover:bg-black px-4 py-2 rounded-sm"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              Log out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#EAE5DE]">
          <button
            onClick={() => setTab("stripe")}
            data-testid="tab-stripe"
            className={`px-5 py-3 text-sm font-bold transition-colors border-b-2 ${
              tab === "stripe"
                ? "border-[#5FBE1F] text-[#1C1C1A]"
                : "border-transparent text-[#4A4A46] hover:text-[#1C1C1A]"
            }`}
          >
            Stripe
          </button>
          <button
            onClick={() => setTab("contacts")}
            data-testid="tab-contacts"
            className={`px-5 py-3 text-sm font-bold transition-colors border-b-2 ${
              tab === "contacts"
                ? "border-[#5FBE1F] text-[#1C1C1A]"
                : "border-transparent text-[#4A4A46] hover:text-[#1C1C1A]"
            }`}
          >
            Contact submissions
            <span className="ml-2 text-xs font-normal text-[#4A4A46]">
              ({contacts.length})
            </span>
          </button>
          <button
            onClick={() => setTab("services")}
            data-testid="tab-services"
            className={`px-5 py-3 text-sm font-bold transition-colors border-b-2 ${
              tab === "services"
                ? "border-[#5FBE1F] text-[#1C1C1A]"
                : "border-transparent text-[#4A4A46] hover:text-[#1C1C1A]"
            }`}
          >
            Member services
            <span className="ml-2 text-xs font-normal text-[#4A4A46]">
              ({services.filter((s) => s.status === "requested" || s.status === "scheduled").length})
            </span>
          </button>
        </div>

        {loading && <div className="text-[#4A4A46]">Loading…</div>}
        {error && (
          <div className="text-red-600 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {tab === "stripe" && !loading && (
          <>
            {/* Overview cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard
                label="MRR"
                value={fmtMoney(overview?.mrr)}
                sub="Monthly recurring revenue"
                icon={TrendingUp}
                testId="stat-mrr"
              />
              <StatCard
                label="Active subs"
                value={overview?.active_subscribers ?? 0}
                sub="Currently paying members"
                icon={Users}
                testId="stat-subs"
              />
              <StatCard
                label="Revenue · 30d"
                value={fmtMoney(overview?.revenue_last_30d)}
                sub={`${overview?.payments_last_30d ?? 0} payments`}
                icon={CreditCard}
                testId="stat-revenue"
              />
              <StatCard
                label="Churn · 30d"
                value={`${overview?.churn_last_30d_pct ?? 0}%`}
                sub={`${overview?.churn_last_30d_count ?? 0} cancellations`}
                icon={AlertTriangle}
                testId="stat-churn"
              />
            </div>

            {/* Revenue by service — KPI bars */}
            <section className="mb-10" data-testid="revenue-breakdown-section">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-2xl text-[#1C1C1A]">
                  Revenue by service
                  <span className="text-sm font-normal text-[#4A4A46] ml-2">
                    (last 30 days)
                  </span>
                </h2>
                <div className="text-sm text-[#4A4A46]">
                  Total {fmtMoney(breakdown?.total_revenue)}
                </div>
              </div>
              {!breakdown || (breakdown.breakdown || []).length === 0 ? (
                <div className="bg-white border border-[#EAE5DE] p-8 rounded-sm text-center text-[#4A4A46]">
                  No paid charges in the last 30 days — once payments come in,
                  this breakdown shows how each service contributes to
                  revenue.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {breakdown.breakdown.map((row) => (
                    <div
                      key={row.service}
                      className="bg-white border border-[#EAE5DE] rounded-sm p-4"
                      data-testid={`kpi-${row.service.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="flex items-baseline justify-between mb-1">
                        <div className="text-sm font-bold text-[#1C1C1A]">
                          {row.service}
                        </div>
                        <div className="font-display text-2xl text-[#1C1C1A]">
                          {row.percent}%
                        </div>
                      </div>
                      <div className="text-xs text-[#4A4A46] mb-2">
                        {fmtMoney(row.revenue)}
                      </div>
                      <div className="h-1.5 bg-[#EAE5DE] rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-[#5FBE1F]"
                          style={{ width: `${Math.min(row.percent, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Active subscriptions */}
            <section className="mb-10" data-testid="subs-section">
              <h2 className="font-display text-2xl text-[#1C1C1A] mb-3">
                Active subscriptions
              </h2>
              {/* Member lookup — quick search before a visit */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A95]" />
                <input
                  type="text"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Look up a member by name or email…"
                  className="w-full bg-white border border-[#EAE5DE] focus:border-[#5FBE1F] outline-none rounded-sm pl-10 pr-4 py-2.5 text-sm"
                  data-testid="member-search"
                />
              </div>
              {filteredSubs.length === 0 ? (
                <div className="bg-white border border-[#EAE5DE] p-8 rounded-sm text-center text-[#4A4A46]">
                  {subs.length === 0 ? (
                    <>
                      No active subscriptions yet. Once customers sign up at{" "}
                      <a href="/plans" className="text-[#5FBE1F] underline">
                        /plans
                      </a>
                      , they&apos;ll show up here.
                    </>
                  ) : (
                    <>No members match &quot;{memberQuery}&quot;.</>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-[#EAE5DE] rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAFAF8] text-xs uppercase tracking-[0.14em] text-[#4A4A46]">
                      <tr>
                        <th className="text-left px-4 py-3">Customer</th>
                        <th className="text-left px-4 py-3">Plan</th>
                        <th className="text-left px-4 py-3">Amount</th>
                        <th className="text-left px-4 py-3">Renews</th>
                        <th className="text-left px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubs.map((s) => (
                        <tr
                          key={s.id}
                          className="border-t border-[#EAE5DE]"
                          data-testid={`sub-row-${s.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="text-[#1C1C1A]">
                              {s.customer_name || s.customer_email || "—"}
                            </div>
                            {s.customer_name && s.customer_email && (
                              <div className="text-xs text-[#4A4A46]">
                                {s.customer_email}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[#1C1C1A]">
                            {s.plan_name}
                          </td>
                          <td className="px-4 py-3 text-[#1C1C1A]">
                            {fmtMoney(s.amount)}
                            <span className="text-xs text-[#4A4A46]">
                              /{s.interval}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#4A4A46]">
                            {fmtDate(s.current_period_end)}
                            {s.cancel_at_period_end && (
                              <div className="text-xs text-[#A84B34]">
                                Cancels at period end
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-block text-xs font-bold uppercase bg-[#5FBE1F]/15 text-[#1E3A2F] px-2 py-1 rounded-sm">
                              {s.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Recent payments */}
            <section data-testid="payments-section">
              <h2 className="font-display text-2xl text-[#1C1C1A] mb-3">
                Recent payments
                <span className="text-sm font-normal text-[#4A4A46] ml-2">
                  (last 30 days)
                </span>
              </h2>
              {payments.length === 0 ? (
                <div className="bg-white border border-[#EAE5DE] p-8 rounded-sm text-center text-[#4A4A46]">
                  No payments in the last 30 days.
                </div>
              ) : (
                <div className="bg-white border border-[#EAE5DE] rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAFAF8] text-xs uppercase tracking-[0.14em] text-[#4A4A46]">
                      <tr>
                        <th className="text-left px-4 py-3">Date</th>
                        <th className="text-left px-4 py-3">Customer</th>
                        <th className="text-left px-4 py-3">Description</th>
                        <th className="text-left px-4 py-3">Amount</th>
                        <th className="text-left px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr
                          key={p.id}
                          className="border-t border-[#EAE5DE]"
                          data-testid={`payment-row-${p.id}`}
                        >
                          <td className="px-4 py-3 text-[#4A4A46]">
                            {fmtDate(p.created)}
                          </td>
                          <td className="px-4 py-3 text-[#1C1C1A]">
                            {p.customer_name || p.customer_email || "—"}
                          </td>
                          <td className="px-4 py-3 text-[#4A4A46]">
                            {p.description}
                          </td>
                          <td className="px-4 py-3 font-bold text-[#1C1C1A]">
                            {fmtMoney(p.amount)}
                            {p.refunded && (
                              <div className="text-xs text-[#A84B34] font-normal">
                                Refunded
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {p.receipt_url && (
                              <a
                                href={p.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-[#5FBE1F] hover:underline"
                              >
                                Receipt <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {tab === "contacts" && !loading && (
          <div className="space-y-4" data-testid="admin-list">
            {contacts.length === 0 ? (
              <div className="bg-white border border-[#EAE5DE] p-10 rounded-sm text-center text-[#4A4A46]">
                No submissions yet.
              </div>
            ) : (
              contacts.map((r) => (
                <article
                  key={r.id}
                  className="bg-white border border-[#EAE5DE] rounded-sm p-6"
                  data-testid={`admin-row-${r.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-display text-xl text-[#1C1C1A]">
                        {r.name}
                      </div>
                      <div className="text-sm text-[#4A4A46]">
                        {r.service_type}
                      </div>
                    </div>
                    <div className="text-xs text-[#4A4A46] text-right">
                      <div>{new Date(r.created_at).toLocaleString()}</div>
                      <div
                        className={
                          r.email_sent ? "text-[#1E3A2F]" : "text-[#A84B34]"
                        }
                      >
                        {r.email_sent ? "Email sent" : "Email not sent"}
                      </div>
                      {r.preferred_date && (
                        <div className="mt-1 text-[#1C1C1A]">
                          Booked: {r.preferred_date}
                          {r.preferred_time_window &&
                            r.preferred_time_window !== "flexible" &&
                            ` · ${r.preferred_time_window}`}
                        </div>
                      )}
                      <button
                        onClick={() => releaseSlot(r)}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#A84B34] hover:text-white hover:bg-[#A84B34] border border-[#E5C9C0] hover:border-[#A84B34] px-2.5 py-1 rounded-sm transition-colors"
                        data-testid={`release-slot-${r.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                        Release slot
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-[#1C1C1A]">
                      <Phone className="w-4 h-4 text-[#5FBE1F]" />
                      <a href={`tel:${r.phone}`} className="hover:underline">
                        {r.phone}
                      </a>
                    </div>
                    {r.email && (
                      <div className="flex items-center gap-2 text-[#1C1C1A]">
                        <Mail className="w-4 h-4 text-[#5FBE1F]" />
                        <a
                          href={`mailto:${r.email}`}
                          className="hover:underline"
                        >
                          {r.email}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[#1C1C1A] sm:col-span-3">
                      <MessageSquare className="w-4 h-4 text-[#5FBE1F]" />
                      <span>{r.address}</span>
                    </div>
                  </div>
                  {r.message && (
                    <div className="mt-4 pt-4 border-t border-[#EAE5DE] text-sm text-[#4A4A46] whitespace-pre-wrap">
                      {r.message}
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
        )}

        {tab === "services" && !loading && (
          <div className="space-y-4" data-testid="admin-services-list">
            <div className="bg-[#FFF7E5] border border-[#FFD86B] rounded-sm p-4 text-sm text-[#7A5B00]">
              <strong>How this works:</strong> When a member requests a service,
              it lands here as &quot;Requested&quot;. Fill in a Scheduled Date + Time
              window and save — that blocks the slot on the public contact
              form. Mark &quot;Completed&quot; once the visit is done.
            </div>
            {services.length === 0 ? (
              <div className="bg-white border border-[#EAE5DE] p-10 rounded-sm text-center text-[#4A4A46]">
                No member service requests yet.
              </div>
            ) : (
              services.map((s) => (
                <ServiceRow
                  key={s.id}
                  row={s}
                  onSave={(patch) => updateService(s, patch)}
                  onDelete={() => deleteService(s)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Service row ----------------
function ServiceRow({ row, onSave, onDelete }) {
  const [scheduledDate, setScheduledDate] = useState(row.scheduled_date || "");
  const [scheduledWindow, setScheduledWindow] = useState(row.scheduled_time_window || "");
  const [notes, setNotes] = useState(row.notes || "");
  const [saving, setSaving] = useState(false);

  const statusColor = {
    requested: "bg-[#FFF3D6] text-[#7A5B00] border-[#FFD86B]",
    scheduled: "bg-[#E5F4D6] text-[#3B5F1A] border-[#5FBE1F]",
    completed: "bg-[#EAE5DE] text-[#4A4A46] border-[#C9C2B6]",
    cancelled: "bg-[#F8D6D6] text-[#7A2C2C] border-[#E0A0A0]",
  };

  async function saveSchedule() {
    setSaving(true);
    try {
      await onSave({
        status: "scheduled",
        scheduled_date: scheduledDate || null,
        scheduled_time_window: scheduledWindow || null,
        notes,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <article
      className="bg-white border border-[#EAE5DE] rounded-sm p-6"
      data-testid={`admin-service-${row.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-display text-xl text-[#1C1C1A]">
            {row.customer_name || row.customer_email}
          </div>
          <div className="text-sm text-[#4A4A46]">{row.service_type}</div>
          <div className="text-xs text-[#4A4A46] mt-1">
            {row.customer_email}
          </div>
        </div>
        <div className="text-right">
          <span
            className={`inline-block text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${statusColor[row.status] || statusColor.requested}`}
            data-testid={`service-status-${row.id}`}
          >
            {row.status}
          </span>
          <div className="text-xs text-[#4A4A46] mt-2">
            Requested {new Date(row.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm">
        <div className="text-[#4A4A46]">
          <strong className="text-[#1C1C1A]">Member asked for:</strong>{" "}
          {row.requested_date || "no date"}{" "}
          {row.requested_time_window && `· ${row.requested_time_window}`}
        </div>
        {row.notes && (
          <div className="mt-1 text-[#4A4A46] italic">&quot;{row.notes}&quot;</div>
        )}
      </div>

      {/* Scheduling controls */}
      <div className="mt-5 pt-5 border-t border-[#EAE5DE] grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4A4A46] mb-1">
            Scheduled date
          </label>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            className="w-full bg-white border border-[#EAE5DE] px-2.5 py-2 rounded-sm text-sm text-[#1C1C1A] focus:border-[#5FBE1F] outline-none"
            data-testid={`service-scheduled-date-${row.id}`}
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4A4A46] mb-1">
            Time window
          </label>
          <select
            value={scheduledWindow}
            onChange={(e) => setScheduledWindow(e.target.value)}
            className="w-full bg-white border border-[#EAE5DE] px-2.5 py-2 rounded-sm text-sm text-[#1C1C1A] focus:border-[#5FBE1F] outline-none"
            data-testid={`service-scheduled-window-${row.id}`}
          >
            <option value="">— pick —</option>
            <option value="08:00">8–9 AM</option>
            <option value="09:00">9–10 AM</option>
            <option value="10:00">10–11 AM</option>
            <option value="11:00">11–12 PM</option>
            <option value="12:00">12–1 PM</option>
            <option value="13:00">1–2 PM</option>
            <option value="14:00">2–3 PM</option>
            <option value="15:00">3–4 PM</option>
            <option value="16:00">4–5 PM</option>
            <option value="17:00">5–6 PM</option>
            <option value="18:00">6–7 PM</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#4A4A46] mb-1">
            Notes
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            className="w-full bg-white border border-[#EAE5DE] px-2.5 py-2 rounded-sm text-sm text-[#1C1C1A] focus:border-[#5FBE1F] outline-none"
            data-testid={`service-notes-${row.id}`}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={saveSchedule}
          disabled={saving || !scheduledDate || !scheduledWindow}
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#5FBE1F] text-black hover:bg-[#4A9818] hover:text-white px-3 py-2 rounded-sm disabled:opacity-50"
          data-testid={`service-save-${row.id}`}
        >
          <CalendarIcon className="w-3.5 h-3.5" />
          {row.status === "scheduled" ? "Update schedule" : "Mark scheduled & block slot"}
        </button>
        {row.status !== "completed" && (
          <button
            onClick={() => onSave({ status: "completed" })}
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#1C1C1A] text-white hover:bg-black px-3 py-2 rounded-sm"
            data-testid={`service-complete-${row.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Mark completed
          </button>
        )}
        {row.status !== "cancelled" && row.status !== "completed" && (
          <button
            onClick={() => onSave({ status: "cancelled" })}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#A84B34] border border-[#E5C9C0] hover:bg-[#A84B34] hover:text-white hover:border-[#A84B34] px-3 py-2 rounded-sm"
            data-testid={`service-cancel-${row.id}`}
          >
            <Clock className="w-3.5 h-3.5" />
            Cancel
          </button>
        )}
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#A84B34] hover:text-white hover:bg-[#A84B34] border border-[#E5C9C0] hover:border-[#A84B34] px-3 py-2 rounded-sm ml-auto"
          data-testid={`service-delete-${row.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </button>
      </div>
    </article>
  );
}
